import React from 'react'
import { injectIntl } from 'react-intl'
import BaseDiagram from './BaseDiagram'
import calculatePartition from './partitions'
import {
  useBlockDiagram,
  useParsePreviewParams,
  usePreviewBlockDiagram,
} from './hooks'
import { getNodeWidthByContent, calculateTextSize } from './nodes/dimensions'
import edgeRouter from './edges/block/Router'
import {
  useDiagram,
  useDiagramDispatch,
} from '../FunctionalDiagrams/DiagramContext'
import nodeRouter from './nodes/block/Router'
import { Loadable } from '../components/Loading'
import { NoImage } from '../FunctionalDiagrams/index'
import {
  createRenderFilters,
  resolveConnector,
  getConnectorNeighbors,
} from './connectorResolution'

export const BlockDiagramPreview = () => {
  const { authToken, componentHashId } = useParsePreviewParams()

  const { isSuccess, isLoading, blockDiagramGraph } = usePreviewBlockDiagram({
    authToken: authToken,
    componentHashId: componentHashId,
  })

  return !isSuccess && !isLoading ? (
    <NoImage />
  ) : (
    <BaseBlockDiagramWrapper
      hideDisconnectedParents={true}
      hideDisconnectedPorts={true}
      isSuccess={isSuccess}
      isLoading={isLoading}
      blockDiagramGraph={blockDiagramGraph}
    />
  )
}

const BlockDiagram = ({
  intl,
  vin,
  subsystemId,
  hideDisconnectedParents = true,
  hideDisconnectedPorts = true,
  theme = null,
}) => {
  const { isSuccess, isLoading, blockDiagramGraph } = useBlockDiagram({
    vin,
    subsystemId,
  })
  return (
    <BaseBlockDiagramWrapper
      hideDisconnectedParents={hideDisconnectedParents}
      hideDisconnectedPorts={hideDisconnectedPorts}
      isSuccess={isSuccess}
      isLoading={isLoading}
      blockDiagramGraph={blockDiagramGraph}
      theme={theme}
    />
  )
}

const BaseBlockDiagramWrapper = ({
  intl,
  hideDisconnectedParents = true,
  hideDisconnectedPorts = true,
  hideSelfLoops = true,
  isSuccess,
  isLoading,
  blockDiagramGraph,
  theme = null,
}) => {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const [graph, setGraph] = React.useState(null)
  const { componentId: selectedComponentId, resolution } =
    diagram.blockDiagram.graph.data

  const filters = React.useMemo(
    () =>
      createRenderFilters({
        hideDisconnectedParents,
        hideDisconnectedPorts,
        hideSelfLoops,
      }),
    [hideDisconnectedParents, hideDisconnectedPorts, hideSelfLoops],
  )

  // A selection made from outside the graph — a table row, or a pinout deep link
  // — is only an identity. Fill in the metadata every consumer reads as soon as
  // the graph is available. Keyed on `resolution` rather than a ref so a graph
  // rebuild (a refetch) cannot replay an already-resolved selection.
  React.useEffect(() => {
    if (!graph || !selectedComponentId || resolution !== 'pending') return

    const metadata = resolveConnector({
      graph,
      componentId: selectedComponentId,
      filters,
    })

    if (metadata) {
      dispatch.blockDiagram.resolveConnector({
        componentId: selectedComponentId,
        metadata,
      })
    } else {
      dispatch.blockDiagram.markConnectorUnresolvable({
        componentId: selectedComponentId,
      })
    }
  }, [graph, selectedComponentId, resolution])

  const getNodeDimensions = ({ parent, ports }) => {
    const height = 40

    const width = getNodeWidthByContent({ parent, ports })

    return {
      width,
      height,
    }
  }

  const onPortClick = ({ graph, port }) =>
    dispatch.blockDiagram.handleConnectorSelection({
      componentId: port.componentId,
      metadata: resolveConnector({
        graph,
        componentId: port.componentId,
        filters,
      }),
    })

  const onNodeClick = ({ graph, node, ids }) => {
    const {
      componentId,
      imageName,
      description,
      alias,
      locationImageName,
      category,
      partNumber,
    } = node.data

    const neighborIds = graph
      .$id(node.id)
      .children()
      .reduce((neighbors, connector) => {
        neighbors.push(...getConnectorNeighbors(connector))
        return neighbors
      }, [])

    dispatch.blockDiagram.handleDeviceSelection({
      description,
      alias,
      componentId,
      imageName,
      ids,
      neighborIds,
      locationImageName,
      category,
      partNumber,
    })
  }

  const onEdgeClick = ({ graph, edge }) => {
    const portsIds = graph
      .$id(edge.id)
      .connectedNodes()
      .map((port) => port.id())
    const fromComponent = graph.$id(edge.fromPort).data('componentId')
    const toComponent = graph.$id(edge.toPort).data('componentId')
    const fromComponentAlias = graph.$id(edge.fromPort).data('alias')
    const toComponentAlias = graph.$id(edge.toPort).data('alias')

    dispatch.blockDiagram.handleConnectionSelection({
      fromComponent,
      toComponent,
      fromComponentAlias,
      toComponentAlias,
      ids: [...portsIds, edge.id],
    })
  }

  const prepareNodes = (graph) => {
    // Hides parent nodes (devices) that are disconnected from other parent nodes
    // via ports, and ports that are disconnected or only self-looping.
    const parents = graph
      .nodes()
      .filter((ele) => ele.isParent())
      .filter(filters.isRenderedParent)

    return parents.map((parent) => {
      const children = parent.children().filter(filters.isRenderedPort)

      const ports = children.map((child) => {
        const { height, width } = calculateTextSize(child.data('label'))

        return {
          ...child.data(),
          id: child.id(),
          borderOffset: 10,
          width: width,
          height: height,
        }
      })

      const { width, height } = getNodeDimensions({ parent, ports })

      return {
        text: parent.data('label'),
        id: parent.id(),
        width,
        height,
        layoutOptions: {
          'org.eclipse.elk.portConstraints': 'FREE',
          'org.eclipse.elk.partitioning.partition': calculatePartition(parent),
        },
        data: {
          ...parent.data(),
        },
        ports,
      }
    })
  }

  const prepareEdges = (graph) => {
    return graph.edges().reduce((result, edge) => {
      const sourceParent = edge.source().parent()
      const targetParent = edge.target().parent()

      // Do not show self loop edges
      if (hideSelfLoops) {
        const selfLoopExists = sourceParent.id() === targetParent.id()
        if (selfLoopExists) return result
      }

      result.push({
        id: edge.id(),
        from: sourceParent.id(),
        to: targetParent.id(),
        fromPort: edge.data('source'),
        toPort: edge.data('target'),
        dataTestId: [edge.source().data('label'), edge.target().data('label')]
          .sort()
          .join('::'),
      })

      return result
    }, [])
  }

  return (
    <>
      <Loadable isLoading={isLoading} showText={false}>
        {isSuccess && (
          <div className="block-diagram">
            <BaseDiagram
              data={blockDiagramGraph}
              preprocess={setGraph}
              prepareEdges={prepareEdges}
              prepareNodes={prepareNodes}
              edgeRouter={edgeRouter}
              nodeRouter={nodeRouter}
              onNodeClick={onNodeClick}
              onPortClick={onPortClick}
              onEdgeClick={onEdgeClick}
              selectionIds={diagram.blockDiagram.graph.data.ids}
              layoutOptions={
                {
                  //org.eclipse.elk.portAlignment': 'CENTER',
                }
              }
              theme={theme}
            />
          </div>
        )}
      </Loadable>
    </>
  )
}

export default injectIntl(BlockDiagram)
